// ===================== Bosses =====================
// Bosses reuse the enemy pipeline but add an update(game, e, dt) hook that
// drives telegraphed attack patterns using bossState for timers.
import { defineBoss } from '../core/registries.js';
import { TAU, angleOf } from '../engine/math.js';

function phaseFactor(e) {
  // Attacks intensify as the boss loses health.
  const frac = e.hp / e.maxHp;
  return frac < 0.33 ? 1.6 : frac < 0.66 ? 1.25 : 1;
}

defineBoss({
  id: 'maw', name: 'THE MAW', sprite: 'boss_maw', behavior: 'brute',
  blurb: 'A mouth with a body attached somewhere behind it.',
  hp: 2600, speed: 40, dmg: 22, r: 52, xp: 120, gold: 25, tint: '#c93a5e',
  update(game, e, dt) {
    const st = e.bossState;
    const pf = phaseFactor(e);
    // Radial bullet spit.
    st.shootT = (st.shootT ?? 1.6) - dt;
    if (st.shootT <= 0) {
      st.shootT = 2.6 / pf;
      const n = 14;
      const off = game.rng.angle();
      for (let i = 0; i < n; i++) {
        const a = off + (i / n) * TAU;
        const sp = 150;
        game.spawnEnemyBullet(e.x, e.y, Math.cos(a) * sp, Math.sin(a) * sp, e.dmg * 0.55, '#ff2d9b');
      }
      game.particles_ring(e.x, e.y, 60, '#ff2d9b');
    }
    // Summon adds.
    st.summonT = (st.summonT ?? 6) - dt;
    if (st.summonT <= 0) {
      st.summonT = 7;
      for (let i = 0; i < 3; i++) {
        const a = game.rng.angle();
        game.spawnEnemy('slime', e.x + Math.cos(a) * 60, e.y + Math.sin(a) * 60);
      }
    }
  },
});

defineBoss({
  id: 'warden', name: 'FROST WARDEN', sprite: 'boss_warden', behavior: 'shooter',
  blurb: 'Artillery in a blizzard’s shape. Keep moving.',
  hp: 3200, speed: 58, dmg: 20, r: 50, xp: 150, gold: 30, tint: '#5ac9e6',
  update(game, e, dt) {
    const st = e.bossState;
    const pf = phaseFactor(e);
    const p = game.player;
    // Aimed 5-shot fan.
    st.fanT = (st.fanT ?? 1.0) - dt;
    if (st.fanT <= 0) {
      st.fanT = 1.5 / pf;
      const base = angleOf(p.x - e.x, p.y - e.y);
      for (let i = -2; i <= 2; i++) {
        const a = base + i * 0.18;
        const sp = 210;
        game.spawnEnemyBullet(e.x, e.y, Math.cos(a) * sp, Math.sin(a) * sp, e.dmg * 0.5, '#bff0ff');
      }
    }
    // Expanding ice ring.
    st.ringT = (st.ringT ?? 4) - dt;
    if (st.ringT <= 0) {
      st.ringT = 5;
      const n = 20; const off = game.rng.angle();
      for (let i = 0; i < n; i++) {
        const a = off + (i / n) * TAU;
        game.spawnEnemyBullet(e.x, e.y, Math.cos(a) * 120, Math.sin(a) * 120, e.dmg * 0.5, '#5ac9e6');
      }
      game.particles_ring(e.x, e.y, 90, '#5ac9e6');
    }
  },
});

defineBoss({
  id: 'void', name: 'VOID TITAN', sprite: 'boss_void', behavior: 'brute',
  blurb: 'The dark, given weight.',
  hp: 4200, speed: 46, dmg: 24, r: 54, xp: 200, gold: 40, tint: '#7a2ac0',
  update(game, e, dt) {
    const st = e.bossState;
    const pf = phaseFactor(e);
    // Rotating spiral emitter.
    st.spiralA = (st.spiralA ?? 0) + dt * 2.4;
    st.spiralT = (st.spiralT ?? 0) - dt;
    if (st.spiralT <= 0) {
      st.spiralT = 0.12 / pf;
      for (let arm = 0; arm < 3; arm++) {
        const a = st.spiralA + arm * (TAU / 3);
        const sp = 165;
        game.spawnEnemyBullet(e.x, e.y, Math.cos(a) * sp, Math.sin(a) * sp, e.dmg * 0.5, '#c98aff');
      }
    }
    // Blink toward the player + summon wisps.
    st.blinkT = (st.blinkT ?? 8) - dt;
    if (st.blinkT <= 0) {
      st.blinkT = 9;
      const p = game.player;
      const a = game.rng.angle();
      e.x = p.x + Math.cos(a) * 360; e.y = p.y + Math.sin(a) * 360;
      game.particles_burst(e.x, e.y, '#c98aff', 24);
      for (let i = 0; i < 2; i++) {
        const aa = game.rng.angle();
        game.spawnEnemy('wisp', e.x + Math.cos(aa) * 50, e.y + Math.sin(aa) * 50);
      }
    }
  },
});

// ------------------------------------------------------------ FINALE BOSSES
// One per region, named by the stage's `finaleBoss`. They arrive when the clock
// runs out, so surviving to one already counts as clearing the hex — killing it
// is the optional climax (and its own achievement). Unlike a scheduled boss
// they land at the run's peak difficulty on top of a 4x boss-count multiplier,
// so their base HP sits near a scheduled boss's while the fight lands far
// heavier. `cry` is the arrival shout the engine announces.

// ---- deferred-detonation helper -------------------------------------------
// Several finale patterns arm a blast that goes off later at a remembered
// point. The slots are allocated once per boss and recycled, so a boss that
// fires every few seconds for the whole fight never allocates in the loop.
function pending(st, n = 10) {
  if (!st.pend) {
    st.pend = [];
    for (let i = 0; i < n; i++) st.pend.push({ on: false, t: 0, x: 0, y: 0, n: 0, sp: 0, dmg: 0, col: '' });
  }
  return st.pend;
}
function arm(st, x, y, t, count, speed, dmg, col) {
  const list = pending(st);
  for (let i = 0; i < list.length; i++) {
    const s = list[i];
    if (s.on) continue;
    s.on = true; s.t = t; s.x = x; s.y = y; s.n = count; s.sp = speed; s.dmg = dmg; s.col = col;
    return s;
  }
  return null;
}
// Tick every armed slot; when its fuse runs out it bursts into a radial ring.
function tickPending(game, st, dt) {
  const list = st.pend;
  if (!list) return;
  for (let i = 0; i < list.length; i++) {
    const s = list[i];
    if (!s.on) continue;
    s.t -= dt;
    if (s.t > 0) continue;
    s.on = false;
    const off = game.rng.angle();
    for (let k = 0; k < s.n; k++) {
      const a = off + (k / s.n) * TAU;
      game.spawnEnemyBullet(s.x, s.y, Math.cos(a) * s.sp, Math.sin(a) * s.sp, s.dmg, s.col);
    }
    game.particles_ring(s.x, s.y, 54, s.col);
  }
}

// Fire a ring with `gaps` evenly spaced three-wide holes in it, rotated by a
// seeded draw so the safe lane is somewhere new every time — the pattern is a
// wall you read and walk through, not a wall you tank.
function gappedRing(game, x, y, n, gaps, speed, dmg, col, off) {
  const step = Math.floor(n / gaps);
  const start = Math.floor(game.rng.range(0, n));
  for (let i = 0; i < n; i++) {
    let skip = false;
    for (let g = 0; g < gaps; g++) {
      const d = Math.abs(i - ((start + g * step) % n));
      if (Math.min(d, n - d) <= 1) { skip = true; break; }
    }
    if (skip) continue;
    const a = off + (i / n) * TAU;
    game.spawnEnemyBullet(x, y, Math.cos(a) * speed, Math.sin(a) * speed, dmg, col);
  }
}

// ---- Crystal Cavern: THE SHARD REGENT --------------------------------------
// Crystalline artillery. It keeps its distance and works the ground instead of
// the player: heavy shards fly slowly and SHATTER wherever they happen to be a
// beat later, so the danger is where the volley will be, not where it is.
// Gapped facet-rings force a read, and each phase break detonates its crown.
defineBoss({
  id: 'shardregent', name: 'THE SHARD REGENT', sprite: 'boss_shardregent',
  blurb: 'Crystal artillery — its shards shatter a beat later, along their own path.',
  behavior: 'shooter', shootCd: 2.6,
  hp: 5000, speed: 64, dmg: 30, r: 54, xp: 420, gold: 90, tint: '#7fd6ff',
  killAch: 'regent_bane',
  cry: '◆  THE SHARD REGENT RISES  ◆',
  update(game, e, dt) {
    const st = e.bossState;
    const pf = phaseFactor(e);
    const p = game.player;
    pending(st);
    tickPending(game, st, dt);

    // Shatter volley: slow shards, each fused to burst along its own path.
    st.volleyT = (st.volleyT ?? 1.4) - dt;
    if (st.volleyT <= 0) {
      st.volleyT = 3.0 / pf;
      const base = angleOf(p.x - e.x, p.y - e.y);
      const sp = 130;
      const fuse = 1.0;
      for (let i = -2; i <= 2; i++) {
        const a = base + i * 0.24;
        const vx = Math.cos(a) * sp, vy = Math.sin(a) * sp;
        game.spawnEnemyBullet(e.x, e.y, vx, vy, e.dmg * 0.42, '#7fd6ff');
        // The burst lands exactly where that shard will be — telegraphed by
        // the shard itself, which is the whole read.
        arm(st, e.x + vx * fuse, e.y + vy * fuse, fuse, 7, 175, e.dmg * 0.34, '#eafcff');
      }
    }

    // Facet ring: a full wall with two holes in it.
    st.ringT = (st.ringT ?? 4.5) - dt;
    if (st.ringT <= 0) {
      st.ringT = 6.5 / pf;
      gappedRing(game, e.x, e.y, 26, 2, 140, e.dmg * 0.4, '#3a8ad1', game.rng.angle());
      game.particles_ring(e.x, e.y, 96, '#7fd6ff');
    }

    // Phase breaks: the crown detonates once at each threshold.
    const frac = e.hp / e.maxHp;
    const stage = frac < 0.33 ? 2 : frac < 0.66 ? 1 : 0;
    if (stage > (st.broke ?? 0)) {
      st.broke = stage;
      const off = game.rng.angle();
      for (let i = 0; i < 20; i++) {
        const a = off + (i / 20) * TAU;
        game.spawnEnemyBullet(e.x, e.y, Math.cos(a) * 230, Math.sin(a) * 230, e.dmg * 0.36, '#eafcff');
      }
      game.particles_burst(e.x, e.y, '#eafcff', 26);
      game.camera.shake(9, 0.3);        // shake/flash self-disable on reduced motion
      game.addFlash('#7fd6ff', 0.3);
    }
  },
});

// ---- Ashen Wastes: THE ASH TYRANT ------------------------------------------
// A slab of burning rock. It walks you down, winds up (the engine's charger
// telegraph) and CHARGES; wherever the charge stops, the ground answers with
// paired shockwaves. It burns a trail behind it the whole time, so the arena
// closes in as the fight runs — and past a third HP it stops pacing itself.
defineBoss({
  id: 'ashtyrant', name: 'THE ASH TYRANT', sprite: 'boss_ashtyrant',
  blurb: 'A telegraphed charge, twin shockwaves, and a burning trail that closes the arena in.',
  behavior: 'charger',
  hp: 3600, speed: 46, dmg: 40, r: 62, xp: 460, gold: 95, tint: '#ff5a1e',
  killAch: 'tyrant_bane',
  cry: '▲  THE ASH TYRANT WAKES  ▲',
  update(game, e, dt) {
    const st = e.bossState;
    const pf = phaseFactor(e);
    pending(st);
    tickPending(game, st, dt);

    // Enrage: one-time, at a third health.
    if (!st.enraged && e.hp / e.maxHp < 0.33) {
      st.enraged = true;
      game.setAnnounce('THE ASH TYRANT ENRAGES', 2.2);
      game.camera.shake(12, 0.4);
      game.addFlash('#ff5a1e', 0.35);
      game.particles_burst(e.x, e.y, '#ffd15c', 30);
    }

    // Ember trail — slow-burning ground it leaves behind (thicker mid-charge).
    st.emberT = (st.emberT ?? 0) - dt;
    if (st.emberT <= 0) {
      st.emberT = (e.dashing ? 0.10 : 0.30) / (st.enraged ? 1.5 : 1);
      const a = game.rng.angle();
      const b = game.spawnEnemyBullet(e.x, e.y, Math.cos(a) * 10, Math.sin(a) * 10, e.dmg * 0.3, '#ff8a3d');
      b.r = 15; b.life = 3.4;           // both fields are re-set on every spawn — no pool leak
    }

    // The charge lands: paired shockwaves out of the impact point.
    const slam = (x, y, waves) => {
      game.camera.shake(11, 0.35);
      game.particles_ring(x, y, 130, '#ff5a1e');
      for (let w = 0; w < waves; w++) {
        arm(st, x, y, w * 0.42, 22, 190 - w * 45, e.dmg * 0.42, w === 0 ? '#ff5a1e' : '#ffd15c');
      }
    };
    if (st.wasDashing && !e.dashing) slam(e.x, e.y, st.enraged ? 3 : 2);
    st.wasDashing = e.dashing;

    // Standing slam, for when it can't reach you — the fight still comes to you.
    st.slamT = (st.slamT ?? 4.5) - dt;
    if (st.slamT <= 0) {
      st.slamT = 6.0 / pf;
      if (!e.dashing) slam(e.x, e.y, st.enraged ? 3 : 2);
    }
  },
});

// ---- The Void: THE NULL ARCHON ---------------------------------------------
// The abyss with a will. It orbits at range and PULLS — the whole fight is
// fought against a current dragging you toward it. It breathes: a long inhale
// that hauls you in hard, then an exhale that detonates the space you were
// dragged into. Between breaths, rings of void collapse inward onto you.
defineBoss({
  id: 'nullarchon', name: 'THE NULL ARCHON', sprite: 'boss_nullarchon',
  blurb: 'Fought against gravity itself — it inhales, then detonates.',
  behavior: 'orbiter', orbitPref: 250,
  hp: 3800, speed: 70, dmg: 34, r: 56, xp: 500, gold: 100, tint: '#a06bff',
  killAch: 'archon_bane',
  cry: '◇  THE NULL ARCHON OPENS  ◇',
  update(game, e, dt) {
    const st = e.bossState;
    const pf = phaseFactor(e);
    const p = game.player;

    // The breath cycle drives everything: 0 = drifting, 1 = inhaling.
    st.breathT = (st.breathT ?? 5.5) - dt;
    if (st.breathT <= 0) {
      if (st.inhaling) {
        // Exhale — the space you were hauled into detonates.
        st.inhaling = false;
        st.breathT = 6.0 / pf;
        const off = game.rng.angle();
        for (let i = 0; i < 26; i++) {
          const a = off + (i / 26) * TAU;
          game.spawnEnemyBullet(e.x, e.y, Math.cos(a) * 235, Math.sin(a) * 235, e.dmg * 0.4, '#ff5cf0');
        }
        game.particles_ring(e.x, e.y, 150, '#ff5cf0');
        game.camera.shake(10, 0.35);
        game.addFlash('#a06bff', 0.3);
      } else {
        st.inhaling = true;
        st.breathT = 1.6;
        game.particles_burst(e.x, e.y, '#c9a2ff', 22);
      }
    }

    // GRAVITY: a constant drag toward the Archon, brutal during the inhale.
    // It never pushes you inside its body — the pull stops at contact range.
    const pull = st.inhaling ? 210 : 34 * pf;
    const dx = e.x - p.x, dy = e.y - p.y;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    if (d > e.r + p.r) {
      const f = Math.min(pull * dt, d - (e.r + p.r));
      p.x += (dx / d) * f; p.y += (dy / d) * f;
    }
    if (st.inhaling) {
      st.wispT = (st.wispT ?? 0) - dt;
      if (st.wispT <= 0) { st.wispT = 0.12; game.particles_spark(p.x, p.y, '#c9a2ff', 3); }
    }

    // Collapse: a ring of void closing in on where you stand, with a way out.
    st.collapseT = (st.collapseT ?? 2.6) - dt;
    if (st.collapseT <= 0 && !st.inhaling) {
      st.collapseT = 4.6 / pf;
      // Kept inside a screen radius on purpose: the whole ring has to be
      // readable when it forms, or converging fire is just an ambush.
      const R = 340;
      const n = 22;
      const off = game.rng.angle();
      const hole = Math.floor(game.rng.range(0, n));
      const sp = 175;
      for (let i = 0; i < n; i++) {
        const gd = Math.abs(i - hole);
        if (Math.min(gd, n - gd) <= 1) continue;      // the one lane out
        const a = off + (i / n) * TAU;
        const bx = p.x + Math.cos(a) * R, by = p.y + Math.sin(a) * R;
        game.spawnEnemyBullet(bx, by, -Math.cos(a) * sp, -Math.sin(a) * sp, e.dmg * 0.38, '#c9a2ff');
      }
      game.particles_ring(p.x, p.y, R, '#6a24a0');
    }
  },
});

// The Meadow's finale: appears when the clock runs out. Immense HP, fast, and
// hits like a truck — the one finale you are meant to outlast rather than beat.
// Surviving to it already counts as clearing the stage.
defineBoss({
  id: 'reaper', name: 'THE REAPER', sprite: 'boss_reaper', behavior: 'brute',
  blurb: 'The end of every clock. Outlasting it is survival — felling it is legend.',
  hp: 90000, speed: 96, dmg: 900, r: 46, xp: 500, gold: 100, tint: '#c8d0e0',
  killAch: 'reaper_bane',      // beating this one is its own achievement
  cry: '⚰  THE REAPER COMES  ⚰',
  update(game, e, dt) {
    const st = e.bossState;
    // Sweeping scythe of bullets in all directions.
    st.reapT = (st.reapT ?? 1.2) - dt;
    if (st.reapT <= 0) {
      st.reapT = 1.4;
      const n = 24;
      const off = (st.off = (st.off ?? 0) + 0.3);
      for (let i = 0; i < n; i++) {
        const a = off + (i / n) * TAU;
        game.spawnEnemyBullet(e.x, e.y, Math.cos(a) * 180, Math.sin(a) * 180, e.dmg * 0.4, '#e8f0ff');
      }
      game.particles_ring(e.x, e.y, 70, '#e8f0ff');
    }
  },
});
